import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, doc, setDoc, getDocs, deleteDoc } from 'firebase/firestore';
import { ACCESS_LEVELS, clearAccessCache } from '../config/accessControl';

const UserAccessManager = () => {
    const [users, setUsers] = useState([]);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserLevel, setNewUserLevel] = useState(ACCESS_LEVELS.PARTNER);
    const [newUserScenarios, setNewUserScenarios] = useState('');
    const [newUserModes, setNewUserModes] = useState(['text']); // Default to text mode only
    const [loading, setLoading] = useState(true);
    const [editingUser, setEditingUser] = useState(null);
    const [editModes, setEditModes] = useState(['text']);
    const [error, setError] = useState(null);

    // Fetch all users
    useEffect(() => {
        const fetchUsers = async () => {
            // Check if user is authenticated
            if (!auth.currentUser) {
                setError('You must be authenticated to manage users.');
                setLoading(false);
                return;
            }

            try {
                console.log('Fetching users with auth user:', auth.currentUser.email);
                try {
                    const querySnapshot = await getDocs(collection(db, 'userAccess'));
                    const usersList = querySnapshot.docs.map(doc => ({
                        email: doc.id,
                        ...doc.data()
                    }));
                    setUsers(usersList);
                } catch (error) {
                    console.error('Error fetching users:', error);
                    // Handle the case when collection doesn't exist
                    if (error.message.includes('Missing or insufficient permissions')) {
                        // Try to initialize the collection
                        const ACCESS_LEVELS = {
                            ADMIN: 'admin',
                            PARTNER: 'partner'
                        };
                        
                        const MASTER_ADMIN = 'a.g.e.bioinf@gmail.com';
                        
                        // Create userAccess collection with master admin
                        const masterAdminData = {
                            level: ACCESS_LEVELS.ADMIN,
                            scenarios: 'all',
                            modes: ['text', 'mixed', 'full-voice'],
                            lastUpdated: new Date()
                        };
                        
                        try {
                            const userRef = doc(db, 'userAccess', MASTER_ADMIN);
                            await setDoc(userRef, masterAdminData);
                            console.log('Master admin user created successfully.');
                            
                            // Now try to fetch users again
                            const querySnapshot = await getDocs(collection(db, 'userAccess'));
                            const usersList = querySnapshot.docs.map(doc => ({
                                email: doc.id,
                                ...doc.data()
                            }));
                            setUsers(usersList);
                        } catch (initError) {
                            console.error('Error creating master admin:', initError);
                            setError(`Error creating collection: ${initError.message}`);
                        }
                    } else {
                        setError(`Error fetching users: ${error.message}`);
                    }
                }
            } catch (finalError) {
                console.error('Unhandled error:', finalError);
                setError(`Error: ${finalError.message}`);
            }
            setLoading(false);
        };

        fetchUsers();
    }, []);

    // Add new user
    const handleAddUser = async (e) => {
        e.preventDefault();
        setError(null);
        
        console.log('Attempting to add user:', newUserEmail, newUserLevel, newUserScenarios);
        
        // Validate inputs
        if (!newUserEmail || !newUserEmail.includes('@')) {
            setError('Please enter a valid email address');
            return;
        }

        if (newUserLevel === ACCESS_LEVELS.PARTNER && !newUserScenarios) {
            setError('Please specify scenarios for partner users');
            return;
        }
        
        try {
            // Show loading
            setLoading(true);
            
            // Prepare data
            const userData = {
                level: newUserLevel,
                scenarios: newUserLevel === ACCESS_LEVELS.ADMIN ? 'all' : newUserScenarios.split(',').map(s => s.trim()),
                modes: newUserLevel === ACCESS_LEVELS.ADMIN ? ['text', 'mixed', 'full-voice'] : newUserModes,
                currentScenario: null, // Initialize with no selected scenario
                lastUpdated: new Date()
            };
            
            console.log('Data to write:', userData);
            
            const userRef = doc(db, 'userAccess', newUserEmail);
            await setDoc(userRef, userData);
            
            console.log('User successfully added to Firebase');

            // Clear the access cache after adding a user
            clearAccessCache();
            console.log('Access cache cleared');

            // Reset form
            setNewUserEmail('');
            setNewUserLevel(ACCESS_LEVELS.PARTNER);
            setNewUserScenarios('');
            setNewUserModes(['text']);

            // Refresh users list
            const querySnapshot = await getDocs(collection(db, 'userAccess'));
            const usersList = querySnapshot.docs.map(doc => ({
                email: doc.id,
                ...doc.data()
            }));
            setUsers(usersList);
        } catch (error) {
            console.error('Error adding user:', error);
            setError(`Error adding user: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Delete user
    const handleDeleteUser = async (email) => {
        if (window.confirm(`Are you sure you want to delete access for ${email}?`)) {
            try {
                setLoading(true);
                await deleteDoc(doc(db, 'userAccess', email));
                // Clear the access cache after deleting a user
                clearAccessCache();
                setUsers(users.filter(user => user.email !== email));
                console.log('User successfully deleted');
            } catch (error) {
                console.error('Error deleting user:', error);
                setError(`Error deleting user: ${error.message}`);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleEditClick = (user) => {
        setEditingUser(user);
        setEditModes(user.modes || ['text']);
    };

    const handleSaveEdit = async () => {
        try {
            setLoading(true);
            
            // Prepare update data
            const updateData = {
                ...editingUser,
                modes: editModes,
                lastUpdated: new Date()
            };
            
            console.log('Updating user with data:', updateData);
            
            const userRef = doc(db, 'userAccess', editingUser.email);
            await setDoc(userRef, updateData, { merge: true });
            console.log('User successfully updated');

            // Clear the access cache after editing
            clearAccessCache();

            // Refresh users list
            const querySnapshot = await getDocs(collection(db, 'userAccess'));
            const usersList = querySnapshot.docs.map(doc => ({
                email: doc.id,
                ...doc.data()
            }));
            setUsers(usersList);
            setEditingUser(null);
        } catch (error) {
            console.error('Error updating user:', error);
            setError(`Error updating user: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">User Access Management</h2>
            
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
                    <span className="block sm:inline">{error}</span>
                </div>
            )}
            
            {/* Add User Form */}
            <form onSubmit={handleAddUser} className="mb-8 space-y-4">
                <div>
                    <input
                        type="email"
                        value={newUserEmail}
                        onChange={(e) => setNewUserEmail(e.target.value)}
                        placeholder="User Email"
                        className="w-full p-2 border rounded"
                        required
                    />
                </div>
                <div>
                    <select
                        value={newUserLevel}
                        onChange={(e) => setNewUserLevel(e.target.value)}
                        className="w-full p-2 border rounded"
                    >
                        <option value={ACCESS_LEVELS.ADMIN}>Admin</option>
                        <option value={ACCESS_LEVELS.PARTNER}>Partner</option>
                    </select>
                </div>
                {newUserLevel === ACCESS_LEVELS.PARTNER && (
                    <>
                        <div>
                            <input
                                type="text"
                                value={newUserScenarios}
                                onChange={(e) => setNewUserScenarios(e.target.value)}
                                placeholder="Scenarios (comma-separated)"
                                className="w-full p-2 border rounded"
                                required
                            />
                        </div>
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Widget Modes
                            </label>
                            <div className="space-y-2">
                                {['text', 'mixed', 'full-voice'].map(mode => (
                                    <label key={mode} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={newUserModes.includes(mode)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setNewUserModes([...newUserModes, mode]);
                                                } else {
                                                    setNewUserModes(newUserModes.filter(m => m !== mode));
                                                }
                                            }}
                                            className="mr-2"
                                        />
                                        {mode.charAt(0).toUpperCase() + mode.slice(1).replace('-', ' ')}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </>
                )}
                <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                    Add User
                </button>
            </form>

            {/* Users List */}
            <div className="space-y-4">
                {users.map(user => (
                    <div key={user.email} className="p-4 border rounded">
                        {editingUser?.email === user.email ? (
                            <div className="space-y-4">
                                <div>
                                    <p className="font-bold">{user.email}</p>
                                    <p>Level: {user.level}</p>
                                    <p>Scenarios: {user.scenarios === 'all' ? 'All' : user.scenarios.join(', ')}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Widget Modes
                                    </label>
                                    <div className="space-y-2">
                                        {['text', 'mixed', 'full-voice'].map(mode => (
                                            <label key={mode} className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={editModes.includes(mode)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setEditModes([...editModes, mode]);
                                                        } else {
                                                            setEditModes(editModes.filter(m => m !== mode));
                                                        }
                                                    }}
                                                    className="mr-2"
                                                />
                                                {mode.charAt(0).toUpperCase() + mode.slice(1).replace('-', ' ')}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={handleSaveEdit}
                                        className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                                    >
                                        Save
                                    </button>
                                    <button
                                        onClick={() => setEditingUser(null)}
                                        className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="font-bold">{user.email}</p>
                                    <p>Level: {user.level}</p>
                                    <p>Scenarios: {user.scenarios === 'all' ? 'All' : user.scenarios.join(', ')}</p>
                                    <p>Widget Modes: {user.modes ? user.modes.join(', ') : 'text'}</p>
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => handleEditClick(user)}
                                        className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDeleteUser(user.email)}
                                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default UserAccessManager; 